import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { apiErrorHandling } from 'src/api/apierrorhandling';
import { deleteRecords } from 'src/api/stoApi';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { AddEditRecordsDialog } from 'src/components/assets/records/AddEditRecordsDialog';
import { DeleteRecordsDialog } from 'src/components/assets/records/DeleteRecordsDialog';
import { RecordsTable } from 'src/components/assets/records/RecordsTable';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useAssetRecords } from 'src/hooks/useAssetRecords';
import { IRecordGetResponse } from 'src/models';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { isNullUndefinedOrEmpty } from 'src/util/utils';
import { Add } from '@mui/icons-material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Button } from '@mui/material';
import { useAsset } from '../../context-providers/AssetContext';
import { NotifyInvestorsDialog } from './notification/NotifyInvestorsDialog';

export const AssetRecordsSite = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const { t } = useTranslation();

  const [reload, setReload] = useState<boolean>(false);
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<IRecordGetResponse | null>(null);
  const [selectedRecordsForDelete, setSelectedRecordsForDelete] = useState<readonly string[]>([]);
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  const { asset } = useAsset();
  const { records, totalRecords } = useAssetRecords(reload, {
    assetId: assetId ? assetId : '',
    offset: page * rowsPerPage,
    limit: rowsPerPage,
  });

  const handleSelectRecordChanged = (selectedRecord: IRecordGetResponse) => {
    setSelectedRecord(selectedRecord);
    setIsAddEditDialogOpen(true);
  };

  const deleteRecordsAndHandleDialog = async () => {
    setIsLoadingDelete(true);
    try {
      await deleteRecords(selectedRecordsForDelete.map((x) => x));
      setReload(!reload);
      setIsDeleteDialogOpen(false);
      setSelectedRecordsForDelete([]);
    } catch (error) {
      apiErrorHandling(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  return (
    <>
      <NotifyInvestorsDialog open={showNotificationDialog} handleClose={() => setShowNotificationDialog(false)} />
      <DeleteRecordsDialog
        open={isDeleteDialogOpen}
        handleClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedRecordsForDelete([]);
        }}
        isLoading={isLoadingDelete}
        handleCloseAndOk={deleteRecordsAndHandleDialog}
      />
      <AddEditRecordsDialog
        open={isAddEditDialogOpen}
        handleClose={() => {
          setIsAddEditDialogOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        reload={() => setReload(!reload)}
        assetId={assetId ?? ''}
      />
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: (isNullUndefinedOrEmpty(asset?.descriptions?.title) ? asset?.id : asset?.descriptions?.title) ?? '',
          },
          {
            link: Routes.ASSETS.RECORDS.replace(':assetId', asset?.id ?? ''),
            label: t('dashboard.breadcrumbs.records'),
          },
        ]}
        headline={asset ? (isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions.title as string)) : ''}
      >
        {asset && (
          <AssetDetailsLinkList
            assetId={asset.id}
            useSerialNumber={asset.useSerialNumber}
            active={'records'}
            isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
            isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
            isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
            securityType={asset?.investmentDetails?.securityType}
          />
        )}
      </SiteHeader>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingBottom: '24px' }}>
        {selectedRecordsForDelete.length > 0 ? (
          <Button color="error" onClick={() => setIsDeleteDialogOpen(true)}>
            {t('dashboard.assets.records.deletebutton')} ({selectedRecordsForDelete?.length})
          </Button>
        ) : (
          <div></div>
        )}

        <div>
          <Button variant="contained" color="primary" sx={{ mr: 2 }} startIcon={<Add />} onClick={() => setShowNotificationDialog(true)}>
            {t('dashboard.assets.records.notifyInvestors')}
          </Button>

          <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setIsAddEditDialogOpen(true)}>
            {t('dashboard.assets.records.newbutton')}
          </Button>
        </div>
      </div>
      <RecordsTable
        data={records}
        selectedRecordsForDelete={selectedRecordsForDelete}
        setSelectedRecordsForDelete={setSelectedRecordsForDelete}
        recordSelected={handleSelectRecordChanged}
        total={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={(newPage) => {
          setPage(newPage);
          setReload(!reload);
        }}
        setRowsPerPage={(newRowsPerPage) => {
          setRowsPerPage(newRowsPerPage);
          setPage(0);
          setReload(!reload);
        }}
      />
    </>
  );
};
